import { Skeleton } from '@h2hspark/ui';
import { Lock, Sparkles } from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router-dom';

import { YOUR_EVENTS_DESTINATION } from '@/member/destinations';
import { PageBackLink } from '@/member/page-back-link';


import { usePublicEvent } from '../event-discovery/api';
import type { PublicEventDetail } from '../event-discovery/types';

import { useMyRegistration } from './api';
import { ALL_EVENTS_BACK_LABEL, RESERVE_MY_SPOT_LABEL } from './constants';
import { RegisteredEventHeader, type EventHeaderSurface } from './registered-event-header-card';

/**
 * **The frame every per-event page shares** — `Web App.dc.html` §2.4, §2.5, §2.6 and §2.7, which
 * open identically: a back link, then the event header card with its stepper, then the page
 * (`MEM-053` AC-1, AC-7).
 *
 * ## Why a shell rather than four copies of the same opening
 *
 * The prototype draws one card on five pages, and `docs/standards/building-from-designs.md` says a
 * shape used more than once is a component rather than layout written per screen. The card itself
 * is `EventHeaderCard` in `packages/ui`; what is here is the two things the card cannot know — the
 * *event*, which every page would otherwise fetch for itself, and what a page does for a member
 * who is **not** registered.
 *
 * ## The two branches, and where each comes from
 *
 * **Registered** gets the header card, the stepper with this page's phase marked, and `children`.
 *
 * **Not registered** gets the prototype's lock card (§2.4 item 3, §2.6 item 2) and nothing else —
 * *no header card and no stepper*, which is `MEM-053` AC-5. The prototype draws the card in both
 * branches, with a *Not registered* pill and a stepper whose every part is shut; a stepper of three
 * closed doors above a card explaining that the door is closed says it twice, and the criterion
 * settles it. Listed in the pull request as a deviation.
 *
 * A **cancelled or refunded** registration is answered `null` by the server and is therefore the
 * unregistered branch, which is the same rule the event page follows (`MEM-017` AC-6).
 *
 * ## It reads the event once
 *
 * `usePublicEvent` and `useMyRegistration` are the event page's own queries, keyed the same way, so
 * a member arriving from the event page finds both already in the cache and the card does not
 * flash. Nothing here decides anything the server has a rule for — `hasEnded` and
 * `openForRegistration` are the server's answers, as they are on the event page.
 */
export function PerEventShell({
  eventId,
  surface,
  locked,
  back,
  children,
}: {
  readonly eventId: string;
  readonly surface: EventHeaderSurface;
  /** The lock card's words for this page — each surface explains its own absence. */
  readonly locked: { readonly title: string; readonly description: string };
  /** The back link, where a page's way back is not Your Events. Defaults to `BackToYourEvents`. */
  readonly back?: React.ReactNode;
  /** The page, for a member with a confirmed registration. */
  readonly children: (context: {
    readonly event: PublicEventDetail;
    readonly confirmationCode: string;
  }) => React.ReactNode;
}): React.ReactElement {
  const query = usePublicEvent(eventId);
  /* **No `useAuthMe` gate here, unlike the event page's.** All four of these routes sit behind
     `ProtectedRoute`, so a signed-out visitor meets the sign-in interstitial and never renders
     this — which makes the "is there a session?" read the event page needs (its route is public)
     one this one cannot learn anything from, and one more thing to settle before the card draws. */
  const registrationQuery = useMyRegistration(eventId, { enabled: true });

  const waiting = query.isPending || registrationQuery.isPending;
  if (waiting) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-5 w-28" />
        <Skeleton className="h-[190px] w-full rounded-xl" />
        <Skeleton className="h-10 w-2/3" />
        <Skeleton className="h-[220px] w-full rounded-xl" />
      </div>
    );
  }

  const event = query.data;
  if (event === undefined) {
    return (
      <div className="space-y-4">
        {back ?? <BackToYourEvents />}
        <h1 className="text-2xl">That event is not here</h1>
        <p className="text-sm text-muted-foreground">
          The link may be wrong, or the organizer may not have published it yet.
        </p>
      </div>
    );
  }

  const registration = registrationQuery.data?.registration ?? null;
  const registered = registration !== null && event.lifecycleState !== 'cancelled';

  return (
    <div className="space-y-6">
      {back ?? <BackToYourEvents />}

      {registered && registration !== null ? (
        <>
          <RegisteredEventHeader
            event={event}
            confirmationCode={registration.confirmationCode}
            surface={surface}
          />
          {children({ event, confirmationCode: registration.confirmationCode })}
        </>
      ) : (
        <LockCard eventId={eventId} reservable={!event.hasEnded && event.openForRegistration} {...locked} />
      )}
    </div>
  );
}

/**
 * The prototype's back link on Matches and Follow-up (§2.4 item 1, §2.7 item 1): *All events*,
 * which in its own navigation is Your Events. Connect and Requests draw none; one way back on every
 * page about an event is better than a page a member has to use the browser's control to leave.
 */
export function BackToYourEvents(): React.ReactElement {
  return (
    <PageBackLink to={YOUR_EVENTS_DESTINATION}>
      {ALL_EVENTS_BACK_LABEL}
    </PageBackLink>
  );
}

/**
 * The lock card (§2.4 item 3): a 560px card, a sunken 60px disc with a `lock`, the heading, the
 * sentence, and the spark *Reserve my spot*.
 *
 * **The button is not drawn for an event nobody can reserve** — one that has happened, or one whose
 * registration is not open. The prototype has no such state; a control that would be refused is the
 * dead affordance `MEM-031` AC-2 refuses one surface over, and the sentence alone is honest.
 */
function LockCard({
  eventId,
  reservable,
  title,
  description,
}: {
  readonly eventId: string;
  readonly reservable: boolean;
  readonly title: string;
  readonly description: string;
}): React.ReactElement {
  return (
    // `mx-auto` (TW 44164787): the design fixes the card's width and says nothing of where it sits,
    // and on a wide screen it sat hard against the left. Brad, 2026-09-23: "narrow card style is
    // fine, but it should be centered within the wider window."
    <section
      data-testid="per-event-locked"
      className="mx-auto max-w-[560px] rounded-xl border border-border-subtle bg-card px-8 py-10 text-center shadow-sm"
    >
      <span
        className="mb-[18px] inline-flex h-[60px] w-[60px] items-center justify-center rounded-pill bg-muted text-text-subtle"
        aria-hidden
      >
        <Lock className="h-7 w-7" />
      </span>
      <h1 className="mb-2 text-2xl tracking-tight">{title}</h1>
      <p className="mx-auto mb-6 max-w-[400px] text-md leading-relaxed text-muted-foreground">
        {description}
      </p>
      {reservable ? (
        <Link
          to={`/app/events/${encodeURIComponent(eventId)}`}
          className="inline-flex items-center gap-2 rounded-pill bg-spark px-[26px] py-[15px] font-brand text-md font-bold text-white shadow-accent"
        >
          <Sparkles className="h-[18px] w-[18px]" aria-hidden />
          {RESERVE_MY_SPOT_LABEL}
        </Link>
      ) : (
        <Link
          to={`/app/events/${encodeURIComponent(eventId)}`}
          className="inline-flex items-center gap-2 rounded-pill border-[1.5px] border-border bg-card px-[22px] py-[13px] font-brand text-md font-bold text-text-body"
        >
          View full event details
        </Link>
      )}
    </section>
  );
}
